import { useCallback, useEffect, useRef } from 'react';
import { useSearchWithAutocompleteContext } from './SearchInputWithAutocompleteProvider';
import { Typeahead } from 'foundations-components/transitional/components/Typeahead';
import { View } from 'foundations-components/transitional/components/View';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const BackgroundFixTempWrapper = styled.div.withConfig({
  displayName: "SearchInputWithAutocomplete__BackgroundFixTempWrapper",
  componentId: "sc-9ndkfx-0"
})(["div{background-color:transparent;}"]);
const useAutocompleteKeyboardInteractions = ({
  onSelectionChange,
  onSearchEnter,
  onSelectionEnter,
  searchInputRef
}) => {
  const {
    listedOptions,
    setSelectedItemIndex,
    selectedItemIndex,
    selectedItem,
    setHideResults,
    searchValue,
    setUserHasSearched
  } = useSearchWithAutocompleteContext();
  const handleSelection = useCallback(index => {
    const item = setSelectedItemIndex(index);
    if (onSelectionChange) onSelectionChange({
      index,
      item
    });
  }, [onSelectionChange, setSelectedItemIndex]);
  const handleKeyDown = useCallback(({
    key
  }) => {
    switch (key) {
      // these actions do not count as search input
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Shift':
      case 'Home':
      case 'End':
        return;
      case 'ArrowUp':
      case 'ArrowDown':
        setHideResults(false);
        if (!listedOptions.length) return;
        if (selectedItemIndex === null) {
          handleSelection(0);
          return;
        }
        if (key === 'ArrowUp' && selectedItemIndex > 0) {
          handleSelection(selectedItemIndex - 1);
          return;
        }
        if (key === 'ArrowDown' && selectedItemIndex < listedOptions.length - 1) {
          handleSelection(selectedItemIndex + 1);
          return;
        }
        break;
      case 'Enter':
        {
          setHideResults(true);
          if (onSelectionEnter && selectedItem && selectedItemIndex !== null) onSelectionEnter({
            index: selectedItemIndex,
            item: selectedItem
          });
          if (onSearchEnter && !selectedItem && !selectedItemIndex && searchValue.trim().length) onSearchEnter(searchValue);
          break;
        }
      default:
        setHideResults(false);
    }
    setUserHasSearched(true);
  }, [handleSelection, listedOptions.length, onSearchEnter, onSelectionEnter, searchValue, selectedItem, selectedItemIndex, setHideResults, setUserHasSearched]);
  useEffect(() => {
    const input = searchInputRef.current;
    input === null || input === void 0 || input.addEventListener('keydown', handleKeyDown);
    return () => {
      input === null || input === void 0 || input.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, searchInputRef]);
};
const useAutocompleteFocusInteractions = ({
  searchInputRef,
  autocompleteRef,
  autofocus
}) => {
  const {
    setHideResults
  } = useSearchWithAutocompleteContext();
  const handleOutsideClick = e => {
    var _autocompleteRef$curr;
    if (!((_autocompleteRef$curr = autocompleteRef.current) !== null && _autocompleteRef$curr !== void 0 && _autocompleteRef$curr.contains(e.target))) setHideResults(true);
  };
  const handleOnFocus = useCallback(() => {
    setHideResults(false);
  }, [setHideResults]);
  useEffect(() => {
    var _searchInputRef$curre;
    if (autofocus) (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
  }, [autofocus, searchInputRef]);
  useEffect(() => {
    const input = searchInputRef.current;
    input === null || input === void 0 || input.addEventListener('focus', handleOnFocus);
    input === null || input === void 0 || input.addEventListener('click', handleOnFocus);
    return () => {
      input === null || input === void 0 || input.removeEventListener('focus', handleOnFocus);
      input === null || input === void 0 || input.removeEventListener('click', handleOnFocus);
    };
  }, [handleOnFocus, searchInputRef]);
  useEffect(() => {
    document.addEventListener('mouseup', handleOutsideClick);
    return () => {
      document.removeEventListener('mouseup', handleOutsideClick);
    };
  });
};
// customisable and reusable component for search inputs that render autocomplete suggestions
export const SearchInputWithAutocomplete = ({
  itemComponent: ItemComponent,
  onSearchChange,
  onClear,
  autofocus,
  getSelectedItemSearchValue,
  autoselectFirst,
  'data-test-id': dataTestId = 'search-input-with-autocomplete',
  onSelectionChange,
  onSearchEnter,
  onSelectionEnter
}) => {
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const {
    clearSelection,
    hideResults,
    options,
    searchValue,
    setSearchValue,
    selectedItem,
    setSelectedItemIndex,
    listedOptions
  } = useSearchWithAutocompleteContext();
  useAutocompleteKeyboardInteractions({
    searchInputRef,
    onSelectionChange,
    onSearchEnter,
    onSelectionEnter
  });
  useAutocompleteFocusInteractions({
    autofocus,
    searchInputRef,
    autocompleteRef
  });
  const handleOnSearchChange = useCallback(e => {
    const newSearchValue = e.target.value;

    // we don't have an 'onClear' callback in UITypeahead so we assume that if
    // the search input is empty the user has cleared the search
    if (onClear && !newSearchValue.length) {
      onClear();
    }
    setSearchValue(newSearchValue);
    if (onSearchChange) onSearchChange(newSearchValue);
    clearSelection();
  }, [clearSelection, onClear, onSearchChange, setSearchValue]);
  const selectedItemSearchValue = getSelectedItemSearchValue && selectedItem ? getSelectedItemSearchValue(selectedItem) : null;
  useEffect(() => {
    if (autoselectFirst && listedOptions !== null && listedOptions !== void 0 && listedOptions.length) {
      setSelectedItemIndex(0);
      if (onSelectionChange) onSelectionChange({
        index: 0,
        item: listedOptions[0]
      });
    }
  }, [autoselectFirst, onSelectionChange, listedOptions, setSelectedItemIndex]);
  return /*#__PURE__*/_jsx(View, {
    ref: autocompleteRef,
    children: /*#__PURE__*/_jsx(BackgroundFixTempWrapper, {
      children: /*#__PURE__*/_jsx(Typeahead, {
        autoFilter: false,
        "data-test-id": dataTestId,
        hideResults: hideResults,
        inputRef: searchInputRef,
        itemComponent: ItemComponent,
        onSearchChange: handleOnSearchChange,
        options: options,
        padding: "0",
        search: selectedItemSearchValue || searchValue // render selected option label on the search input instead of the user's query if available, for keyboard navigation or item suggestion click
      })
    })
  });
};
export default SearchInputWithAutocomplete;